/** SUNTRONIX ONE — ONE Process Studio
 *
 *  Visual configuration for business process lifecycles.  Structured
 *  form + read-only lane preview + simulator + analytics.  Sits at
 *  /administration/process-studio and is also linked from
 *  Configuration Studio.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Workflow, Play, CheckCircle2, XCircle, Plus, Save, RefreshCw,
  ArrowRight, Trash2, ShieldCheck, GitBranch, TrendingUp, Clock,
  AlertTriangle, ArrowLeft,
} from "lucide-react";

const TABS = [
  { id: "overview",  label: "Overview" },
  { id: "stages",    label: "Stages" },
  { id: "transitions", label: "Transitions" },
  { id: "rules",     label: "Validation" },
  { id: "approvals", label: "Approvals" },
  { id: "actions",   label: "Actions" },
  { id: "simulate",  label: "Simulate" },
  { id: "analytics", label: "Analytics" },
  { id: "history",   label: "History" },
];

const QUOTATION_STARTER = {
  module: "quotation",
  name: "Standard Quotation Process",
  description: "Draft → Review → Issued → Awaiting Customer → Accepted",
  stages: [
    { id: "DRAFT",              label: "Draft",             kind: "START", responsible_role: "sales_coordinator" },
    { id: "INTERNAL_REVIEW",    label: "Internal Review",   kind: "STATE", responsible_role: "management" },
    { id: "SENT",               label: "Issued / Sent",     kind: "STATE", responsible_role: "sales_coordinator", protected: true },
    { id: "ACCEPTED",           label: "Accepted",          kind: "END",   responsible_role: "sales_coordinator" },
    { id: "REJECTED",           label: "Declined",          kind: "END" },
    { id: "EXPIRED",            label: "Expired",           kind: "END" },
  ],
  transitions: [
    { id: "SUBMIT_FOR_REVIEW", label: "Submit for Review",
      from_stage: "DRAFT", to_stage: "INTERNAL_REVIEW",
      who: ["sales_coordinator", "management"],
      required_fields: ["customer_account_id", "subject", "items"] },
    { id: "ISSUE_QUOTATION", label: "Issue Quotation",
      from_stage: "INTERNAL_REVIEW", to_stage: "SENT",
      who: ["management", "super_admin"],
      required_fields: ["customer_account_id", "subject", "items", "customer_email"],
      approval_required: true },
    { id: "MARK_ACCEPTED", label: "Mark Accepted",
      from_stage: "SENT", to_stage: "ACCEPTED",
      who: ["sales_coordinator", "management"] },
    { id: "MARK_REJECTED", label: "Mark Declined",
      from_stage: "SENT", to_stage: "REJECTED",
      who: ["sales_coordinator", "management"] },
  ],
  validation_rules: [
    { id: "REQUIRE_DESCRIPTION", label: "Product description must be present",
      condition: { empty: "product_description" },
      action: { prevent_transition: "ISSUE_QUOTATION" },
      severity: "BLOCK" },
    { id: "REGULAR_GST_NEEDS_GSTIN",
      label: "Regular GST customers require GSTIN",
      condition: { and: [
        { field: "gst_treatment", op: "==", value: "REGULAR" },
        { empty: "customer_gstin" },
      ] },
      action: { prevent_transition: "ISSUE_QUOTATION" },
      severity: "BLOCK" },
  ],
  approval_rules: [
    { id: "MGMT_APPROVAL_HIGH_VALUE",
      label: "Management approval for grand total > ₹5,00,000",
      kind: "SINGLE",
      when: { field: "grand_total_inr", op: ">", value: 500000 },
      approvers: [{ role: "management" }] },
  ],
  action_rules: [
    { id: "NOTIFY_CUSTOMER_ON_ISSUE", label: "Email customer on Issue",
      kind: "NOTIFY_CUSTOMER",
      config: { template: "quote_issued", require_admin_enable: true } },
    { id: "START_FOLLOWUP_ON_ISSUE", label: "Start follow-up plan on Issue",
      kind: "START_FOLLOWUP", config: {} },
  ],
};

export default function ProcessStudio() {
  const [sp, setSp] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [module, setModule] = useState(sp.get("module") || "quotation");
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(sp.get("id") || null);
  const [tab, setTab] = useState(sp.get("tab") || "overview");
  const [proc, setProc] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/process-studio/meta").then(({ data }) => setMeta(data))
        .catch(() => toast.error("Load failed"));
  }, []);

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/process-studio", { params: { module } });
      setRows(data.items || []);
      if (!selectedId && data.items?.length) setSelectedId(data.items[0].id);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  }, [module, selectedId]);
  useEffect(() => { loadList(); }, [loadList]);

  const loadProc = useCallback(async () => {
    if (!selectedId) { setProc(null); return; }
    try {
      const { data } = await api.get(`/process-studio/${selectedId}`);
      setProc(data.process);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  }, [selectedId]);
  useEffect(() => { loadProc(); }, [loadProc]);

  const canAdmin = meta?.current_user?.can_admin;

  const createStarter = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/process-studio", QUOTATION_STARTER);
      toast.success(`Created ${data.process.name} v${data.process.version} (DRAFT)`);
      setSelectedId(data.process.id);
      await loadList();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    if (!proc) return;
    if (!window.confirm(`Publish "${proc.name}" v${proc.version}?\n\n`
      + "This activates this process and deactivates the previous ACTIVE version. "
      + "Historical records remain governed by the version they started under.")) return;
    setBusy(true);
    try {
      await api.post(`/process-studio/${proc.id}/publish`);
      toast.success(`Activated v${proc.version}`);
      await Promise.all([loadList(), loadProc()]);
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
    finally { setBusy(false); }
  };

  const deactivate = async () => {
    if (!proc) return;
    if (!window.confirm(`Deactivate "${proc.name}" v${proc.version}?`)) return;
    try {
      await api.post(`/process-studio/${proc.id}/deactivate`);
      toast.success("Deactivated");
      await Promise.all([loadList(), loadProc()]);
    } catch (e) { toast.error(e.response?.data?.detail || "Deactivate failed"); }
  };

  const setTabAndUrl = (t) => {
    setTab(t);
    const s = new URLSearchParams(sp);
    s.set("tab", t);
    if (selectedId) s.set("id", selectedId);
    s.set("module", module);
    setSp(s, { replace: true });
  };

  return (
    <div className="max-w-[1500px] mx-auto space-y-4" data-testid="process-studio">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-violet-800 text-white grid place-items-center">
            <Workflow className="h-5 w-5" strokeWidth={1.6} />
          </div>
          <div>
            <div className="text-xl font-heading font-semibold text-slate-900">
              ONE Process Studio
            </div>
            <div className="text-[11px] text-slate-500">
              Governed no-code lifecycle configuration · never a new engine
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/administration/config-studio"
              data-testid="ps-open-config-studio"
              className="text-[12px] border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1">
            <ArrowLeft className="h-3.5 w-3.5" /> Configuration Studio
          </Link>
        </div>
      </header>

      {!canAdmin && (
        <div className="border border-amber-200 bg-amber-50/40 rounded-lg p-3 text-[12px] text-amber-800"
            data-testid="ps-readonly-banner">
          You are viewing Process Studio in read-only mode. Only super_admin
          may create or edit processes.
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <select value={module} onChange={(e) => { setModule(e.target.value); setSelectedId(null); }}
            className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5"
            data-testid="ps-module-select">
          {(meta?.modules || []).map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={selectedId || ""} onChange={(e) => setSelectedId(e.target.value)}
            className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5 min-w-[280px]"
            data-testid="ps-process-select">
          <option value="">— Select a process —</option>
          {rows.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name} · v{r.version} · {r.status}
            </option>
          ))}
        </select>
        {canAdmin && (
          <button onClick={createStarter} disabled={busy}
              className="text-[12px] px-3 py-1.5 rounded-md bg-slate-900 text-white inline-flex items-center gap-1"
              data-testid="ps-create-starter">
            <Plus className="h-3 w-3" /> Create starter (Quotation)
          </button>
        )}
        {canAdmin && proc?.status === "DRAFT" && (
          <button onClick={publish} disabled={busy}
              className="text-[12px] px-3 py-1.5 rounded-md bg-emerald-700 text-white inline-flex items-center gap-1"
              data-testid="ps-publish">
            <Play className="h-3 w-3" /> Publish v{proc.version}
          </button>
        )}
        {canAdmin && proc?.status === "ACTIVE" && (
          <button onClick={deactivate}
              className="text-[12px] px-3 py-1.5 rounded-md border border-rose-200 text-rose-700 inline-flex items-center gap-1"
              data-testid="ps-deactivate">
            <XCircle className="h-3 w-3" /> Deactivate
          </button>
        )}
        {canAdmin && proc && proc.status !== "DRAFT" && (
          <button onClick={async () => {
              try {
                const { data } = await api.post(`/process-studio/${proc.id}/clone`);
                toast.success(`Cloned to v${data.process.version} DRAFT`);
                setSelectedId(data.process.id);
                await loadList();
              } catch (e) { toast.error(e.response?.data?.detail || "Clone failed"); }
            }}
              className="text-[12px] px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1"
              data-testid="ps-clone">
            <Plus className="h-3 w-3" /> Clone as new DRAFT
          </button>
        )}
        <div className="ml-auto text-[11px] text-slate-500">
          {rows.length} process(es) · {(rows.filter((r) => r.status === "ACTIVE").length)} active
        </div>
      </div>

      <div className="border-b border-slate-200 flex flex-wrap gap-1">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTabAndUrl(t.id)}
              data-testid={`ps-tab-${t.id}`}
              className={"px-3 py-1.5 text-[12px] -mb-px border-b-2 " +
                            (tab === t.id ? "border-violet-800 text-violet-900 font-medium"
                                                          : "border-transparent text-slate-500 hover:text-slate-900")}>
            {t.label}
          </button>
        ))}
      </div>

      {!proc ? (
        <div className="border border-dashed border-slate-300 rounded-lg p-10 text-center text-[12px] text-slate-500"
            data-testid="ps-empty">
          {rows.length === 0 && canAdmin
            ? <>No processes for <b>{module}</b> yet. Click <b>Create starter (Quotation)</b> to seed a governed process.</>
            : "Pick a process above to view or edit."}
        </div>
      ) : (
        <div className="space-y-4">
          {tab === "overview"    && <Overview proc={proc} />}
          {tab === "stages"      && <StageList proc={proc} />}
          {tab === "transitions" && <TransitionList proc={proc} />}
          {tab === "rules"       && <RuleList proc={proc} kind="validation_rules" />}
          {tab === "approvals"   && <RuleList proc={proc} kind="approval_rules" />}
          {tab === "actions"     && <RuleList proc={proc} kind="action_rules" />}
          {tab === "simulate"    && <SimulatePanel proc={proc} />}
          {tab === "analytics"   && <AnalyticsPanel proc={proc} />}
          {tab === "history"     && <HistoryPanel proc={proc} />}
        </div>
      )}
    </div>
  );
}


function Overview({ proc }) {
  return (
    <div className="space-y-3" data-testid="ps-overview">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <Stat label="Module"   value={proc.module} />
        <Stat label="Version"  value={"v" + proc.version} />
        <Stat label="Status"   value={proc.status} tone={proc.status === "ACTIVE" ? "ok" : "muted"} />
        <Stat label="Stages"   value={proc.stages?.length ?? 0} />
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-3">
        <div className="text-[11px] font-semibold text-slate-700 mb-2">
          Visual lane (read-only preview)
        </div>
        <LanePreview stages={proc.stages || []} transitions={proc.transitions || []} />
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-3 text-[11px] text-slate-600">
        <div className="flex items-center gap-2 font-semibold text-slate-800 mb-1">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-700" /> System-protected
        </div>
        Tenant isolation, authentication, immutable audit, system IDs, and
        issued-document immutability cannot be overridden here. Attempted
        edits to protected transitions are refused with a 403.
      </div>
    </div>
  );
}


function LanePreview({ stages, transitions }) {
  if (!stages.length) return <div className="text-[11px] text-slate-400">No stages defined yet.</div>;
  return (
    <div className="overflow-x-auto" data-testid="ps-lane-preview">
      <div className="flex items-center gap-2 min-w-max">
        {stages.map((s, i) => {
          const outgoing = transitions.filter((t) => t.from_stage === s.id);
          const tone =
            s.kind === "START" ? "bg-sky-50 border-sky-200 text-sky-900"
              : s.kind === "END" ? "bg-slate-100 border-slate-300 text-slate-800"
              : s.kind === "APPROVAL" ? "bg-amber-50 border-amber-200 text-amber-900"
              : "bg-white border-slate-200 text-slate-800";
          return (
            <div key={s.id} className="flex items-center gap-2">
              <div className={"rounded-md border px-3 py-2 min-w-[140px] " + tone}
                data-testid={`ps-lane-${s.id}`}>
                <div className="text-[10px] uppercase tracking-wider opacity-70">{s.kind}</div>
                <div className="text-[13px] font-semibold">{s.label}</div>
                <div className="text-[9px] font-mono text-slate-500">{s.id}</div>
                {outgoing.length > 0 && (
                  <div className="mt-1 text-[9px] text-slate-500">
                    {outgoing.length} exit{outgoing.length > 1 ? "s" : ""}
                  </div>
                )}
              </div>
              {i < stages.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function StageList({ proc }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="ps-stages">
      <table className="min-w-full text-[11px]">
        <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
          <tr>
            <th className="p-2 text-left">ID</th>
            <th className="p-2 text-left">Label</th>
            <th className="p-2 text-left">Kind</th>
            <th className="p-2 text-left">Responsible</th>
            <th className="p-2 text-right">SLA (h)</th>
            <th className="p-2 text-left">Protected</th>
          </tr>
        </thead>
        <tbody>
          {(proc.stages || []).map((s) => (
            <tr key={s.id} className="border-t border-slate-100" data-testid={`ps-stage-row-${s.id}`}>
              <td className="p-2 font-mono">{s.id}</td>
              <td className="p-2 font-semibold">{s.label}</td>
              <td className="p-2">{s.kind}</td>
              <td className="p-2">{s.responsible_role || "—"}</td>
              <td className="p-2 text-right">{s.sla_hours ?? "—"}</td>
              <td className="p-2">{s.protected ? "SYSTEM" : "—"}</td>
            </tr>
          ))}
          {!proc.stages?.length && (
            <tr><td colSpan={6} className="p-6 text-center text-slate-400">No stages defined.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


function TransitionList({ proc }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="ps-transitions">
      <table className="min-w-full text-[11px]">
        <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
          <tr>
            <th className="p-2 text-left">Transition</th>
            <th className="p-2 text-left">From → To</th>
            <th className="p-2 text-left">Who</th>
            <th className="p-2 text-left">Required Fields</th>
            <th className="p-2 text-left">Approval</th>
          </tr>
        </thead>
        <tbody>
          {(proc.transitions || []).map((t) => (
            <tr key={t.id} className="border-t border-slate-100" data-testid={`ps-trans-row-${t.id}`}>
              <td className="p-2">
                <div className="font-semibold">{t.label}</div>
                <div className="font-mono text-slate-500 text-[10px]">{t.id}</div>
              </td>
              <td className="p-2 font-mono text-[10px]">{t.from_stage} → {t.to_stage}</td>
              <td className="p-2">{(t.who || []).join(", ") || "—"}</td>
              <td className="p-2 text-[10px] font-mono">{(t.required_fields || []).join(", ") || "—"}</td>
              <td className="p-2">{t.approval_required ? "Required" : "—"}</td>
            </tr>
          ))}
          {!proc.transitions?.length && (
            <tr><td colSpan={5} className="p-6 text-center text-slate-400">No transitions defined.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


function RuleList({ proc, kind }) {
  const rules = proc?.[kind] || [];
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`ps-${kind}`}>
      <table className="min-w-full text-[11px]">
        <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
          <tr>
            <th className="p-2 text-left">ID</th>
            <th className="p-2 text-left">Label</th>
            <th className="p-2 text-left">Human-readable summary</th>
          </tr>
        </thead>
        <tbody>
          {rules.map((r) => (
            <tr key={r.id} className="border-t border-slate-100">
              <td className="p-2 font-mono">{r.id}</td>
              <td className="p-2 font-semibold">{r.label}</td>
              <td className="p-2 text-slate-600 font-mono text-[10px]">
                {ruleSummary(r, kind)}
              </td>
            </tr>
          ))}
          {!rules.length && (
            <tr><td colSpan={3} className="p-6 text-center text-slate-400">
              No {kind.replace("_", " ")} defined.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


function ruleSummary(r, kind) {
  if (kind === "validation_rules") {
    return `IF ${condToText(r.condition)} THEN ${r.severity === "BLOCK" ? "prevent" : "warn"} (${r.action?.prevent_transition || "any transition"})`;
  }
  if (kind === "approval_rules") {
    return `IF ${condToText(r.when)} THEN ${r.kind} approval by ${(r.approvers || []).map(a => a.role || a.email).join(" / ")}`;
  }
  if (kind === "action_rules") {
    return `${r.kind}${r.config?.template ? " · template=" + r.config.template : ""}`;
  }
  return "";
}
function condToText(c) {
  if (!c || typeof c !== "object") return "—";
  if (c.and) return "(" + c.and.map(condToText).join(" AND ") + ")";
  if (c.or)  return "(" + c.or.map(condToText).join(" OR ") + ")";
  if (c.empty) return `${c.empty} is empty`;
  if (c.not_empty) return `${c.not_empty} is present`;
  if (c.field) return `${c.field} ${c.op || "=="} ${JSON.stringify(c.value)}`;
  return JSON.stringify(c);
}


function SimulatePanel({ proc }) {
  const [transId, setTransId] = useState(proc.transitions?.[0]?.id || "");
  const [json, setJson] = useState(JSON.stringify({
    customer_account_id: "cust-demo-001",
    subject: "Demo simulation",
    items: [{ product_name: "MacBook Pro 14\"", quantity: 1, unit_price_inr: 260000 }],
    grand_total_inr: 300000,
    customer_email: "buyer@example.com",
    gst_treatment: "REGULAR",
    customer_gstin: "",
    product_description: "",
  }, null, 2));
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    let record;
    try { record = JSON.parse(json); }
    catch { toast.error("Sample record must be valid JSON"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/process-studio/simulate",
        { process_id: proc.id, record, transition: transId });
      setResult(data.simulation);
      toast[data.simulation.ok ? "success" : "info"](
        data.simulation.ok ? "Simulation passed" : "Simulation blocked — see findings");
    } catch (e) { toast.error(e.response?.data?.detail || "Simulate failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ps-simulate">
      <div className="bg-white border border-slate-200 rounded-lg p-3">
        <div className="text-[11px] font-semibold text-slate-700 mb-2">Sample record</div>
        <select value={transId} onChange={(e) => setTransId(e.target.value)}
            className="mb-2 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5"
            data-testid="ps-sim-transition">
          {(proc.transitions || []).map((t) =>
            <option key={t.id} value={t.id}>{t.label} ({t.from_stage} → {t.to_stage})</option>)}
        </select>
        <textarea value={json} onChange={(e) => setJson(e.target.value)}
            rows={14} data-testid="ps-sim-json"
            className="w-full border border-slate-200 rounded-md px-2 py-2 font-mono text-[11px]" />
        <button onClick={run} disabled={busy}
            className="mt-2 w-full text-[12px] py-2 rounded-md bg-violet-800 text-white inline-flex items-center justify-center gap-1"
            data-testid="ps-sim-run">
          <Play className="h-3 w-3" /> {busy ? "Simulating…" : "Run simulation"}
        </button>
        <div className="mt-2 text-[10px] text-slate-500">
          Simulation NEVER touches production business records.
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-3">
        <div className="text-[11px] font-semibold text-slate-700 mb-2">Findings</div>
        {!result ? (
          <div className="text-[11px] text-slate-400">Run a simulation to see the outcome.</div>
        ) : (
          <>
            <div className={"text-[13px] font-semibold mb-2 " +
                (result.ok ? "text-emerald-700" : "text-rose-700")}
                data-testid="ps-sim-result">
              {result.ok ? "PASS — transition would proceed" : "BLOCKED — see findings"}
            </div>
            {result.findings.length === 0 && (
              <div className="text-[11px] text-slate-500">No blocking findings.</div>
            )}
            <ul className="space-y-1 text-[11px]">
              {result.findings.map((f, i) => (
                <li key={i} className={"px-2 py-1.5 rounded border " +
                    (f.level === "BLOCK" ? "border-rose-200 bg-rose-50 text-rose-800"
                                                        : "border-amber-200 bg-amber-50 text-amber-800")}>
                  <div className="font-semibold">{f.kind}</div>
                  <div>{f.detail}</div>
                </li>
              ))}
            </ul>
            {result.approvals_needed?.length > 0 && (
              <>
                <div className="mt-3 text-[11px] font-semibold text-slate-700">Approvals required</div>
                <ul className="space-y-1 text-[11px] mt-1">
                  {result.approvals_needed.map((a) => (
                    <li key={a.rule} className="px-2 py-1.5 rounded border border-amber-200 bg-amber-50 text-amber-900">
                      {a.label} · <span className="font-mono">{a.kind}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {result.actions_after?.length > 0 && (
              <>
                <div className="mt-3 text-[11px] font-semibold text-slate-700">Actions on success</div>
                <ul className="space-y-1 text-[11px] mt-1">
                  {result.actions_after.map((a, i) => (
                    <li key={i} className="px-2 py-1.5 rounded border border-slate-200 bg-slate-50">
                      {a.kind || a.type || JSON.stringify(a)}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}


function AnalyticsPanel({ proc }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/process-studio/analytics/${proc.id}`)
        .then(({ data }) => setData(data))
        .catch(() => toast.error("Load failed"));
  }, [proc.id]);
  if (!data) return <div className="text-[12px] text-slate-500">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="ps-analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Records entered"  value={data.entered} />
        <Stat label="Completed"        value={data.completed} tone="ok" />
        <Stat label="Active"           value={Math.max(0, data.entered - data.completed)} />
        <Stat label="Bottleneck"       value={(data.per_stage || []).slice().sort((a, b) => b.count - a.count)[0]?.label || "—"} />
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-3">
        <div className="text-[11px] font-semibold text-slate-700 mb-2">Per-stage counts</div>
        {(data.per_stage || []).map((s) => (
          <div key={s.stage} className="flex items-center justify-between text-[11px] py-1 border-b border-slate-100 last:border-0">
            <span className="text-slate-700">{s.label}</span>
            <span className="font-mono">{s.count}</span>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-slate-400">{data.notes}</div>
    </div>
  );
}


function HistoryPanel({ proc }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/process-studio/${proc.id}/history`).then(({ data }) => setRows(data.items || []))
        .catch(() => toast.error("Load failed"));
  }, [proc.id]);
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="ps-history">
      <table className="min-w-full text-[11px]">
        <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
          <tr>
            <th className="p-2 text-left">When</th>
            <th className="p-2 text-left">Op</th>
            <th className="p-2 text-left">Actor</th>
            <th className="p-2 text-left">Detail</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100">
              <td className="p-2 text-slate-500 font-mono text-[10px]">{r.at?.slice(0, 19)}</td>
              <td className="p-2 font-semibold">{r.op}</td>
              <td className="p-2">{r.actor}</td>
              <td className="p-2 text-slate-600">{r.detail}</td>
            </tr>
          ))}
          {!rows.length && (
            <tr><td colSpan={4} className="p-6 text-center text-slate-400">No history yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


function Stat({ label, value, tone }) {
  const tint = tone === "ok" ? "text-emerald-800 bg-emerald-50 border-emerald-200"
             : tone === "muted" ? "text-slate-700 bg-slate-100 border-slate-200"
             : "text-slate-700 bg-white border-slate-200";
  return (
    <div className={"border rounded p-2.5 " + tint}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-[16px] font-heading font-semibold mt-0.5">{value ?? "—"}</div>
    </div>
  );
}
